import {produce} from 'immer'

import isEmpty from 'lodash/isEmpty'
import mapKeys from 'lodash/mapKeys'
import {createSelector} from 'reselect'

import type {State} from '../../../../reducers/types'
import type {PipelineId} from '../../../../types'
import {mapValues} from '../../../../utils/object'
import {ParameterInputPrefix} from '../contexts/ParametersControl/ParametersControlContext.const'
import type {NewPipeline} from '../services/pipelinesApi.types'
import type {Job, SuggestionType} from '../types'
import {getIsGitVcsRoot} from '../utils/getIsGitVcsRoot'
import {
  isValueConsistentWithArray,
  isValueNotEmptyMessage,
  isValueValidUrl,
  matchError,
} from '../utils/validators'
import type {GetValidators, ValidatorAdditionalProps} from '../utils/validators.types'

import {
  getDependencyName,
  getPipelineDependency,
  getPreformattingSettings,
  getVcsRootReference,
  parsePipeline,
  stringifyPipeline,
} from './EditPipelinePage.utils'
import {IntegrationType} from './SettingsSidebarContent/Integrations/Integrations.consts'
import {jobNameValidators} from './SettingsSidebarContent/JobSettingsSidebar/JobName/JobNameValidationError.consts'
import {
  errorValidators,
  stepFieldValidate,
} from './SettingsSidebarContent/JobSettingsSidebar/JobSteps/JobStep/SmartDetectors/CliErrorMessage/useCliErrorMessage.consts'
import {pipelineNameValidators} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineName/PipelineNameValidationError.consts'
import {
  parameterNameValidators,
  parameterValueValidators,
} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineParameters/PipelineParameterForm/usePipelineParameterForm.const'
import {
  getRepositoryKey,
  ROOT_REPOSITORY_KEY,
} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineRepositories/PipelineRepository/PipelineRepository.utils'
import {
  secretNameValidators,
  secretValueValidators,
} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineSecrets/PipelineSecretForm/PipelineSecretForm.consts'
import {TriggerType} from './SettingsSidebarContent/PipelineSettingsSidebar/PipelineTriggers/PipelineTriggers.consts'
import {ALL_BRANCHES} from './SettingsSidebarContent/PipelineSettingsTriggerSidebar/PipelineOnChangesTriggerForm/PipelineOnChangesTriggerForm.const'
import type {PipelineError} from './slices/EditPipelinePage.slices'
import {MIN_PARALLELISM_COUNT} from './slices/EditPipelinePage.slices.consts'
import type {PipelineDraft, PipelineFormParams} from './slices/EditPipelinePage.slices.types'
import {FormType} from './slices/EditPipelinePage.slices.types'
import {deleteIfEmpty, getPipelineFormKey} from './slices/EditPipelinePage.slices.utils'

type ResolvedSettings = NewPipeline & {
  stringificationError?: string
}

const getOriginal = (state: State, id: string) => state.pipelines.pipelineDraft[id]?.original

export const getOriginalPipelineSettings = (id: string) => (state: State) =>
  state.pipelines.pipelineDraft[id]?.original?.settings

export const getOriginalVersionedSettings = (id: string) => (state: State) =>
  state.pipelines.pipelineDraft[id]?.original?.versionedSettings

export const getDraft = (state: State, id: string) => state.pipelines.pipelineDraft[id]?.draft

export const getPipeline = (state: State, id: string) => state.pipelines.pipelineDraft[id]

export const getDraftJobs = (state: State, id: string) => getDraft(state, id)?.settings.jobs
export const getSteps = (state: State, id: string, jobId: string) =>
  getDraftJobs(state, id)?.[jobId]?.steps
export const getFeatures = (state: State, id: string, jobId: string) =>
  getDraftJobs(state, id)?.[jobId]?.features

export const makePipelineErrorsSelector = () =>
  createSelector(
    [
      (state: State) => state.pipelines.pipelineErrors,
      (_: State, {pipelineId}: PipelineError) => pipelineId,
      (_: State, {jobId}: PipelineError) => jobId,
      (_: State, {fieldName}: PipelineError) => fieldName,
      (_: State, {message}: PipelineError) => message,
    ],
    (errors, pipelineId, jobId, fieldName, message) =>
      errors.filter(matchError({pipelineId, jobId, fieldName, message})),
  )

export const makeTotalCountPipelineErrorsSelector = () =>
  createSelector([makePipelineErrorsSelector()], errors => errors.length)

export const getPipelineErrorMessage = (filter: PipelineError) => (state: State) =>
  state.pipelines.pipelineErrors.find(matchError(filter))?.message

export const getJobName = (state: State, pipelineId: PipelineId, id: string) =>
  getDraftJobs(state, pipelineId)?.[id]?.name ?? id

export const getRunsOn = (jobId: string, id: string) => (state: State) =>
  getDraft(state, id)?.settings?.jobs?.[jobId]?.['runs-on']

export const getOriginalRunsOn = (jobId: string, id: string) => (state: State) =>
  getOriginal(state, id)?.settings?.jobs?.[jobId]?.['runs-on']

export const getJobAllowReuse = (jobId: string, id: string) => (state: State) =>
  getDraft(state, id)?.settings?.jobs?.[jobId]?.['allow-reuse'] !== false

export const isPipelineFormOpened = (state: State, {pipelineId, ...rest}: PipelineFormParams) =>
  state.pipelines.pipelineForm[pipelineId]?.[getPipelineFormKey(rest)]?.open
export const isPipelineFormAdd = (state: State, {pipelineId, ...rest}: PipelineFormParams) =>
  state.pipelines.pipelineForm[pipelineId]?.[getPipelineFormKey(rest)]?.isAdd

export const getSkippedSuggestions = (state: State, stepId: string) =>
  state.pipelines.suggestions.skippedSuggestions[stepId]

export const isSuggestionSkipped = (state: State, stepId: string, type: SuggestionType) =>
  getSkippedSuggestions(state, stepId)?.includes(type)

export const isSuccessMessageVisible = (state: State, stepId: string, type: SuggestionType) =>
  state.pipelines.suggestions.successMessages[stepId] === type

export const isDraftChanged = createSelector(
  (state?: PipelineDraft) => state?.deleted,
  (state?: PipelineDraft) => state?.renamed,
  (state?: PipelineDraft) => state?.reordered,
  (state?: PipelineDraft) => state?.draft?.settings?.jobs,
  (deleted, renamed, reordered, jobs) =>
    Boolean(
      deleted ||
      renamed ||
      reordered ||
      Object.values(jobs ?? {}).some(job =>
        job.features?.some(feature => feature.deleted === true),
      ),
    ),
)

export const isDraftSettingsChanged = createSelector(
  (state?: PipelineDraft) => {
    const {jobs, ...deleted} = state?.deleted ?? {}
    return isEmpty(deleted) ? null : deleted
  },
  (state?: PipelineDraft) => state?.renamed,
  (state?: PipelineDraft) => state?.added,
  (deleted, renamed, added) => Boolean(deleted || renamed || added),
)

const getResolvedSettingsFromDraft = createSelector(
  (state: PipelineDraft) => state?.draft,
  (state: PipelineDraft, params?: GetResolvedSettingsOptions) =>
    params?.excludeDeleted ? null : state?.deleted,
  (state: PipelineDraft) => state?.renamed,
  (state: PipelineDraft, params?: GetResolvedSettingsOptions) =>
    params?.excludeReordered ? null : state?.reordered,
  (draft, deleted, renamed, reordered): ResolvedSettings | null => {
    let stringificationError: string | undefined
    const result = produce(draft, pipeline => {
      if (pipeline != null) {
        if (pipeline.integrations != null) {
          if (deleted?.integrations != null) {
            pipeline.integrations = pipeline.integrations.filter(
              item => !deleted.integrations?.includes(item.id!),
            )
          }
          pipeline.integrations.forEach(({type, parameters}) => {
            switch (type) {
              case IntegrationType.DOCKER:
                parameters.repositoryUrl = parameters.repositoryUrl?.trim() ?? ''
                if (parameters.userName != null) {
                  parameters.userName = parameters.userName.trim()
                }
                break
              case IntegrationType.NPM:
                parameters.npmRegistryHost = parameters.npmRegistryHost?.trim() ?? ''
                if (parameters.npmRegistryScope != null) {
                  parameters.npmRegistryScope = parameters.npmRegistryScope.trim()
                }
                break
              default:
            }
          })
        }
        if (
          pipeline.additionalVcsRoots != null &&
          (deleted?.additionalVcsRoots?.length || deleted?.incompleteAdditionalVcsRoots?.length)
        ) {
          pipeline.additionalVcsRoots = pipeline.additionalVcsRoots.filter((root, index) => {
            const reference = getVcsRootReference(root)
            return reference
              ? !deleted?.additionalVcsRoots?.includes(reference)
              : !deleted?.incompleteAdditionalVcsRoots?.includes(index)
          })
        }
        const {settings} = pipeline
        if (settings.jobs != null) {
          deleted?.jobs?.forEach(id => {
            delete settings.jobs![id]
          })
          Object.entries(settings.jobs).forEach(([jobId, job]) => {
            if (deleted?.steps?.[jobId] != null || reordered?.steps?.[jobId] != null) {
              job.steps = job.steps
                ?.map((_, i) => {
                  const originalIndex = reordered?.steps?.[jobId]?.[i] ?? i
                  return {
                    value: job.steps![originalIndex],
                    isDeleted: deleted?.steps?.[jobId]?.includes(originalIndex),
                  }
                })
                .filter(({isDeleted}) => !isDeleted)
                .map(({value}) => value)
            }
            if (
              job.parallelism != null &&
              (!job.parallelism || job.parallelism < MIN_PARALLELISM_COUNT)
            ) {
              delete job.parallelism
            }
            if (job.repositories?.length && deleted?.additionalVcsRoots?.length) {
              job.repositories = job.repositories?.filter(repository => {
                const [url] = Object.keys(repository)

                return !deleted?.additionalVcsRoots?.includes(url)
              })

              if (!job.repositories?.length) {
                deleteIfEmpty(job, 'repositories')
              }
            }

            if (job.parameters != null) {
              deleted?.jobParameters?.forEach(name => {
                delete job.parameters![name]
              })
              if (renamed?.jobParameters != null) {
                job.parameters = mapKeys(
                  job.parameters,
                  (_, name) => renamed.jobParameters![name] ?? name,
                )
              }

              deleteIfEmpty(job, 'parameters')
            }

            if (job['files-publication'] != null) {
              job['files-publication'] = job['files-publication'].filter(
                (_, index) => !deleted?.filesPublication?.includes(`${jobId}_${index}`),
              )

              deleteIfEmpty(job, 'files-publication')
            }
            if (job.features?.length) {
              job.features = job.features.filter(feature => feature.deleted !== true)
              deleteIfEmpty(job, 'features')
            }
          })
        }
        if (settings.parameters != null) {
          deleted?.parameters?.forEach(name => {
            delete settings.parameters![name]
          })
          if (renamed?.parameters != null) {
            settings.parameters = mapKeys(
              settings.parameters,
              (_, name) => renamed.parameters![name] ?? name,
            )
          }
          deleteIfEmpty(settings, 'parameters')
        }
        if (settings['output-parameters'] != null) {
          deleted?.outputParameters?.forEach(name => {
            delete settings['output-parameters']![name]
          })
          if (renamed?.outputParameters != null) {
            settings['output-parameters'] = mapKeys(
              settings['output-parameters'],
              (_, name) => renamed.outputParameters![name] ?? name,
            )
          }
          deleteIfEmpty(settings, 'output-parameters')
        }
        if (settings.dependencies != null) {
          deleted?.dependencies?.forEach(dependencyId => {
            const index = settings?.dependencies?.findIndex(dependency =>
              getPipelineDependency(dependency, dependencyId),
            )
            if (index != null) {
              settings?.dependencies?.splice(index, 1)
            }
          })

          deleteIfEmpty(settings, 'dependencies')
        }
        if (settings['import-parameters'] != null) {
          deleted?.importParameters?.forEach(name => {
            settings['import-parameters'] = settings['import-parameters']?.filter(
              item => item !== name,
            )
          })
          deleteIfEmpty(settings, 'import-parameters')
        }
        if (settings.secrets != null) {
          deleted?.secrets?.forEach(name => {
            delete settings.secrets![name]
          })
          if (renamed?.secrets != null) {
            settings.secrets = mapKeys(
              settings.secrets,
              (_, name) => renamed.secrets![name] ?? name,
            )
          }

          deleteIfEmpty(settings, 'secrets')
        }

        if (pipeline.notifications != null && deleted?.notifications?.length) {
          pipeline.notifications = pipeline.notifications.filter(
            notification =>
              !deleted.notifications?.includes(
                pipeline.notifications.indexOf(notification).toString(),
              ),
          )
        }

        try {
          pipeline.yaml = stringifyPipeline(settings, {
            replacer: getPreformattingSettings,
            noRefs: true,
          })
        } catch (error) {
          stringificationError = error instanceof Error ? error.message : String(error)
        }
      }
    })

    if (!result) {
      return null
    }

    if (result.settings['import-parameters'] != null) {
      const returnValue: ResolvedSettings = {
        ...result,
        settings: {
          ...result.settings,
          'import-parameters': Array.from(result.settings['import-parameters']),
        },
      } as ResolvedSettings
      if (stringificationError) {
        returnValue.stringificationError = stringificationError
      }
      return returnValue
    }

    const returnValue: ResolvedSettings = {
      ...result,
    } as ResolvedSettings
    if (stringificationError) {
      returnValue.stringificationError = stringificationError
    }
    return returnValue
  },
)

export const getJobEnableDependencyCache = (jobId: string, id: string) => (state: State) =>
  getDraft(state, id)?.settings?.jobs?.[jobId]?.['enable-dependency-cache']

type GetResolvedSettingsOptions = {
  excludeDeleted?: boolean
  excludeReordered?: boolean
}

export const getResolvedSettings = (
  state: State,
  pipelineId: PipelineId,
  params?: GetResolvedSettingsOptions,
) => getResolvedSettingsFromDraft(state.pipelines.pipelineDraft[pipelineId] ?? {}, params)

const yamlSelector = createSelector(
  [
    (state: State, props: {pipelineId: PipelineId}) =>
      state.pipelines.pipelineYamlValidity[props.pipelineId]?.yaml,
    (state: State, props: {pipelineId: PipelineId}) =>
      state.pipelines.pipelineYamlValidity[props.pipelineId]?.isParsed,
    (state: State, props: {pipelineId: PipelineId}) =>
      getResolvedSettings(state, props.pipelineId)?.settings,
  ],
  (invalidYaml, isParsed, settings) => {
    if (!isParsed) {
      return invalidYaml ?? ''
    }
    if (!settings) {
      return ''
    }
    try {
      return (
        stringifyPipeline(settings, {
          replacer: getPreformattingSettings,
          noRefs: true,
        }) ?? ''
      )
    } catch (error) {
      return invalidYaml ?? ''
    }
  },
)

export const getYaml = (state: State, pipelineId: PipelineId) => {
  const {yaml = '', settings} = getOriginal(state, pipelineId) ?? {}
  const yamlFromDraft = yamlSelector(state, {pipelineId})
  const yamlFromOriginal =
    settings &&
    (() => {
      try {
        return stringifyPipeline(settings, {
          replacer: getPreformattingSettings,
          noRefs: true,
        })
      } catch (error) {
        return ''
      }
    })()
  return yamlFromDraft === yamlFromOriginal ? yaml : yamlFromDraft
}

export function getNewYaml(state: State, pipelineId: PipelineId) {
  return state.pipelines.pipelineYamlDraft[pipelineId]?.yaml ?? getYaml(state, pipelineId)
}

export const getNewSettings = (state: State, pipelineId: PipelineId) => {
  try {
    return parsePipeline(getNewYaml(state, pipelineId))
  } catch {
    return {}
  }
}

export function getStringifiedDependencies(jobs?: Record<string, Job>) {
  const result: Record<string, string[]> = {}
  if (jobs != null) {
    Object.entries(jobs).forEach(([id, job]) => {
      result[id] = job.dependencies?.map(getDependencyName) ?? []
    })
  }
  return JSON.stringify(result)
}

export function getAllFormsToValidate(state: State, pipelineId: PipelineId): PipelineFormParams[] {
  const {draft, deleted} = state.pipelines.pipelineDraft[pipelineId] ?? {}
  if (draft == null) {
    return []
  }
  const {settings, additionalVcsRoots = [], integrations = []} = draft
  const {
    jobs = {},
    parameters = {},
    'output-parameters': outputParameters = {},
    secrets = {},
    dependencies = [],
  } = settings
  const jobIds = Object.keys(jobs).filter(id => !deleted?.jobs?.includes(id))
  return [
    {pipelineId, type: FormType.NAME},
    ...Object.keys(parameters)
      .filter(name => !deleted?.parameters?.includes(name))
      .map(name => ({pipelineId, id: name, type: FormType.PARAMETER})),
    ...Object.keys(outputParameters)
      .filter(name => !deleted?.outputParameters?.includes(name))
      .map(name => ({pipelineId, id: name, type: FormType.OUTPUT_PARAMETER})),
    ...Object.keys(secrets)
      .filter(name => !deleted?.secrets?.includes(name))
      .map(name => ({pipelineId, id: name, type: FormType.SECRET})),
    ...integrations
      .filter(integration => integration.id && !deleted?.integrations?.includes(integration.id))
      .map(integration => ({pipelineId, id: integration.id, type: FormType.PIPELINE_INTEGRATION})),
    ...dependencies
      .filter(dependency => !deleted?.dependencies?.includes(Object.keys(dependency)[0]))
      .map(dependency => ({pipelineId, id: Object.keys(dependency)[0], type: FormType.DEPENDENCY})),
    {pipelineId, id: getRepositoryKey(true), type: FormType.REPOSITORY},
    {pipelineId, id: getRepositoryKey(true), type: FormType.TRIGGER},
    ...additionalVcsRoots
      .filter((root, index) => {
        const reference = getVcsRootReference(root)
        return reference
          ? !deleted?.additionalVcsRoots?.includes(reference)
          : !deleted?.incompleteAdditionalVcsRoots?.includes(index)
      })
      .map((_, index) => ({
        pipelineId,
        id: getRepositoryKey(false, index),
        type: FormType.REPOSITORY,
      })),
    ...jobIds.flatMap(jobId => [
      {pipelineId, jobId, type: FormType.NAME},
      ...(jobs[jobId].steps
        ?.map((_, index) => index)
        .filter(index => !deleted?.steps?.[jobId]?.includes(index))
        .map(index => ({pipelineId, jobId, id: index, type: FormType.STEP})) ?? []),
      ...Object.keys(jobs[jobId].parameters ?? {})
        .filter(name => !deleted?.jobParameters?.includes(name))
        .map(name => ({pipelineId, id: name, type: FormType.JOB_PARAMETER, jobId})),
    ]),
  ]
}
function getAllJobParameterNames(
  state: State,
  pipelineId: PipelineId,
  jobId?: string | null,
): string[] {
  const {draft, deleted, renamed} = state.pipelines.pipelineDraft[pipelineId] ?? {}
  if (draft == null || !jobId) {
    return []
  }
  const {parameters = {}} = draft.settings?.jobs?.[jobId] ?? {}

  return Object.keys(parameters)
    .filter(name => !deleted?.jobParameters?.includes(name))
    .map(name => renamed?.jobParameters?.[name] ?? name)
}

function getAllParameterNames(state: State, pipelineId: PipelineId): string[] {
  const {draft, deleted, renamed} = state.pipelines.pipelineDraft[pipelineId] ?? {}
  if (draft == null) {
    return []
  }
  const {parameters} = draft.settings
  return Object.keys(parameters ?? {})
    .filter(name => !deleted?.parameters?.includes(name))
    .map(name => renamed?.parameters?.[name] ?? name)
}

function getAllOutputParameterNames(state: State, pipelineId: PipelineId): string[] {
  const {draft, deleted, renamed} = state.pipelines.pipelineDraft[pipelineId] ?? {}
  if (draft == null) {
    return []
  }
  const outputParameters = draft.settings['output-parameters']
  return Object.keys(outputParameters ?? {})
    .filter(name => !deleted?.outputParameters?.includes(name))
    .map(name => renamed?.outputParameters?.[name] ?? name)
}

function getAllSecretNames(state: State, pipelineId: PipelineId): string[] {
  const {draft, deleted, renamed} = state.pipelines.pipelineDraft[pipelineId] ?? {}
  if (draft == null) {
    return []
  }
  const {secrets} = draft.settings
  return Object.keys(secrets ?? {})
    .filter(name => !deleted?.secrets?.includes(name))
    .map(name => renamed?.secrets?.[name] ?? name)
}

function getAllImportedParameterNames(state: State, pipelineId: PipelineId): string[] {
  const {draft, deleted} = state.pipelines.pipelineDraft[pipelineId] ?? {}
  if (draft == null) {
    return []
  }
  const importParams = draft.settings['import-parameters']
  if (!Array.isArray(importParams)) {
    return []
  }
  return importParams.filter(name => !deleted?.importParameters?.includes(name))
}

const getRepositoryByFormId = (
  state: State,
  pipelineId: PipelineId,
  id: string | number | undefined,
) => {
  const {vcsRoot, additionalVcsRoots} = state.pipelines.pipelineDraft[pipelineId]?.draft ?? {}
  return id === ROOT_REPOSITORY_KEY ? vcsRoot : additionalVcsRoots?.[Number(id)]
}

function getAllTrackPullRequestsValues(state: State, pipelineId: PipelineId): boolean[] {
  const mainTrackPullRequests =
    state.pipelines.pipelineDraft[pipelineId]?.draft?.vcsRoot?.trackPullRequests ?? false
  const additionalVcsRoots =
    state.pipelines.pipelineDraft[pipelineId]?.draft?.additionalVcsRoots ?? []
  const values = [mainTrackPullRequests]
  const deleted = state.pipelines.pipelineDraft[pipelineId]?.deleted?.additionalVcsRoots
  if (!additionalVcsRoots) {
    return values
  }
  return additionalVcsRoots
    .filter(vcsRoot => {
      const reference = getVcsRootReference(vcsRoot)
      return reference != null ? !deleted?.includes(reference) : false
    })
    .map(value => value.trackPullRequests ?? false)
    .concat(values)
}

function getVcsTriggerPullRequestValue(state: State, pipelineId: PipelineId): boolean {
  const triggers = state.pipelines.pipelineDraft[pipelineId]?.draft?.triggers
  const triggerPullRequests = triggers?.find(
    trigger => trigger.type === TriggerType.VCS && !trigger.disabled,
  )?.triggerPullRequests

  return triggerPullRequests ?? false
}

function getVcsTriggerBranchFilterValue(state: State, pipelineId: PipelineId): string {
  const triggers = state.pipelines.pipelineDraft[pipelineId]?.draft?.triggers
  const trigger = triggers?.find(trigger => trigger.type === TriggerType.VCS && !trigger.disabled)
  const branchFilter = trigger?.parameters?.branchFilter ?? ''

  return trigger?.triggerBranches ? branchFilter : ALL_BRANCHES
}

export const validatorSelectors: Partial<Record<FormType, Record<string, GetValidators>>> = {
  [FormType.NAME]: {
    name: (state, {pipelineId, jobId}, additionalProps: ValidatorAdditionalProps | undefined) =>
      jobId != null
        ? {
            value: getJobName(state, pipelineId, jobId),
            compareArray: Object.keys(getDraftJobs(state, pipelineId) ?? {}).map(otherJobId =>
              getJobName(state, pipelineId, otherJobId),
            ),
            validators: jobNameValidators,
          }
        : {
            value: getDraft(state, pipelineId)?.name,
            relatedArray:
              additionalProps?.pipelineNames
                ?.filter(pipeline => pipeline.pipelineId !== pipelineId)
                .map(pipeline => pipeline.pipelineName) || [],
            validators: pipelineNameValidators,
          },
  },
  [FormType.STEP]: mapValues<typeof errorValidators, Record<string, GetValidators>>(
    errorValidators,
    (validators, fieldName) =>
      (state, {pipelineId, jobId, id}) => {
        const step = getSteps(state, pipelineId, jobId!)?.[Number(id)]
        const definedFields = stepFieldValidate[step?.type ?? '']?.has(fieldName)
        const value =
          (step?.[fieldName] as string | boolean | undefined) ?? (definedFields ? '' : undefined)

        return {
          value,
          validators,
        }
      },
  ),
  [FormType.JOB_PARAMETER]: {
    name: (state, {pipelineId, jobId, id}) => {
      let value =
        state.pipelines.pipelineDraft[pipelineId]?.renamed?.jobParameters?.[id!] ?? String(id)

      Object.values(ParameterInputPrefix).forEach(prefix => {
        if (value.startsWith(prefix)) {
          value = value.replace(prefix, '')
        }
      })

      return {
        value,
        compareArray: getAllJobParameterNames(state, pipelineId, jobId),
        relatedArray: getAllSecretNames(state, pipelineId),
        importedParametersArray: getAllImportedParameterNames(state, pipelineId),
        validators: parameterNameValidators,
      }
    },
  },
  [FormType.PARAMETER]: {
    name: (state, {pipelineId, id}) => ({
      value: state.pipelines.pipelineDraft[pipelineId]?.renamed?.parameters?.[id!] ?? String(id),
      compareArray: getAllParameterNames(state, pipelineId),
      relatedArray: getAllSecretNames(state, pipelineId),
      importedParametersArray: getAllImportedParameterNames(state, pipelineId),
      validators: parameterNameValidators,
    }),
    value: (state, {pipelineId, id}) => ({
      value: state.pipelines.pipelineDraft[pipelineId]?.draft?.settings.parameters?.[id!] ?? '',
      validators: parameterValueValidators,
    }),
  },
  [FormType.OUTPUT_PARAMETER]: {
    name: (state, {pipelineId, id}) => ({
      value:
        state.pipelines.pipelineDraft[pipelineId]?.renamed?.outputParameters?.[id!] ?? String(id),
      compareArray: getAllOutputParameterNames(state, pipelineId),
      validators: parameterNameValidators,
    }),
    value: (state, {pipelineId, id}) => ({
      value:
        state.pipelines.pipelineDraft[pipelineId]?.draft?.settings['output-parameters']?.[id!] ??
        '',
      validators: parameterValueValidators,
    }),
  },
  [FormType.SECRET]: {
    name: (state, {pipelineId, id}) => ({
      value: state.pipelines.pipelineDraft[pipelineId]?.renamed?.secrets?.[id!] ?? String(id),
      compareArray: getAllSecretNames(state, pipelineId),
      relatedArray: getAllParameterNames(state, pipelineId),
      validators: secretNameValidators,
    }),
    value: (state, {pipelineId, id}) => ({
      value: state.pipelines.pipelineDraft[pipelineId]?.draft?.settings.secrets?.[id!] ?? '',
      validators: secretValueValidators,
    }),
  },
  [FormType.TRIGGER]: {
    pullRequests: (state, {pipelineId}) => ({
      value: getVcsTriggerPullRequestValue(state, pipelineId),
      compareArray: getAllTrackPullRequestsValues(state, pipelineId),
      validators: [
        {
          validator: isValueConsistentWithArray,
          message:
            'Invalid auto-run trigger settings: the trigger is set to run on pull requests that the pipeline does not monitor. Ensure the trigger configuration aligns with the pipeline’s “Repository” settings.',
        },
      ],
    }),
    branchFilter: (state, {pipelineId}) => ({
      value: getVcsTriggerBranchFilterValue(state, pipelineId),
      validators: [
        {
          validator: isValueNotEmptyMessage,
          message: 'Branch filter should not be empty',
        },
      ],
    }),
  },
  [FormType.REPOSITORY]: {
    url: (state, {pipelineId, id}) => {
      const {vcsName, isExternal} = getRepositoryByFormId(state, pipelineId, id) ?? {}
      return {
        disabled: isExternal || !getIsGitVcsRoot(vcsName),
        value: getRepositoryByFormId(state, pipelineId, id)?.url,
        validators: [
          {
            validator: isValueNotEmptyMessage,
            message: 'Repository URL should not be empty',
          },
        ],
      }
    },
    branch: (state, {pipelineId, id}) => {
      const {url, branch, isExternal} = getRepositoryByFormId(state, pipelineId, id) ?? {}
      return {
        disabled: !url || isExternal,
        value: branch,
        validators: [
          {
            validator: isValueNotEmptyMessage,
            message: 'Branch name should not be empty',
          },
        ],
      }
    },
  },
  [FormType.REPOSITORIES]: {
    pullRequests: (state, {pipelineId}) => ({
      value: getVcsTriggerPullRequestValue(state, pipelineId),
      compareArray: getAllTrackPullRequestsValues(state, pipelineId),
      validators: [
        {
          validator: isValueConsistentWithArray,
          message:
            'Invalid auto-run trigger settings: the trigger is set to run on pull requests that the pipeline does not monitor. Ensure the trigger configuration aligns with the pipeline’s “Repository” settings.',
        },
      ],
    }),
  },
  [FormType.PIPELINE_INTEGRATION]: {
    url: (state, {pipelineId, id}) => {
      const integration = state.pipelines.pipelineDraft[pipelineId]?.draft?.integrations?.find(
        item => item.id === id,
      )
      const url =
        integration?.parameters?.npmRegistryHost ?? integration?.parameters?.repositoryUrl ?? ''

      return {
        value: url,
        validators: [
          {
            validator: isValueValidUrl,
            message: 'Invalid URL',
          },
        ],
      }
    },
  },
  [FormType.DEPENDENCY]: {
    dependency: (_, {pipelineId, id}) => ({
      value: id === pipelineId ? '' : id?.toString(),
      validators: [
        {
          validator: isValueNotEmptyMessage,
          message: 'Dependency should not be empty',
        },
      ],
    }),
  },
}

export const getPipelineErrors = (state: State, filter: PipelineError) =>
  state.pipelines.pipelineErrors.filter(matchError(filter))

export const getIsPipelineValid = createSelector(
  [
    (state: State, props: {pipelineId: PipelineId}) =>
      !state.pipelines.pipelineYamlDraft[props.pipelineId]?.diagnostics?.length,
    (state: State, props: {pipelineId: PipelineId}) =>
      state.pipelines.pipelineYamlValidity[props.pipelineId]?.isParsed ?? true,
  ],
  (isValid, isParsed) => isValid && isParsed,
)

export const getIsStoreInRepoEnabled = createSelector(
  [
    (state: State, props: {pipelineId: PipelineId}) =>
      state.pipelines.pipelineDraft[props.pipelineId]?.draft?.versionedSettings?.storedInRepo,
    (state: State, props: {pipelineId: PipelineId}) =>
      state.pipelines.pipelineDraft[props.pipelineId]?.original?.versionedSettings?.storedInRepo,
  ],
  (storedInRepoDraft, storedInRepoOriginal) => !storedInRepoOriginal && storedInRepoDraft,
)

export const getIsDebugRunning = (state: State, pipelineId: PipelineId) =>
  state.pipelines.debug[pipelineId]?.isRunning

export const getDrafParamtersChangesWatcher = createSelector(
  [
    (state: State, pipelineId: PipelineId) => getPipeline(state, pipelineId)?.added?.parameters,
    (state: State, pipelineId: PipelineId) => getPipeline(state, pipelineId)?.deleted?.parameters,
  ],
  (addedJobParameters, deletedJobParameters) => [
    ...Object.keys(addedJobParameters ?? {}),
    ...Object.keys(deletedJobParameters ?? {}),
  ],
)

export const getStringificationError = (state: State, pipelineId: PipelineId) => {
  const resolvedSettings = getResolvedSettings(state, pipelineId)
  return resolvedSettings?.stringificationError
}
